import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ROUTES, API_DOMAIN } from '../../../Reusable_Components/Config/apiConfig';
import { ToastContext } from '../../../../context/ToastContext';
import ConfirmModal from '../../../Reusable_Components/ConfirmModal';
import * as listCache from '../../../../services/listCache';
import { fmtDate } from '../../../../utils/fmt';

// ── Auth ─────────────────────────────────────────────────────────────────────
const isSuperAdmin = () => {
  try {
    if (localStorage.getItem('is_superadmin') === 'true') return true;
    return !!JSON.parse(localStorage.getItem('user') || '{}').is_superadmin;
  } catch { return false; }
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function buildHeaders() {
  const token = localStorage.getItem('customer_management_token');
  const tenantId = localStorage.getItem('crm_tenant_id');
  const tenantToken = localStorage.getItem('crm_tenant_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
    ...(tenantToken ? { 'X-Tenant-Token': tenantToken } : {}),
  };
}

function pageRange(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current, current - 1, current + 1].filter(p => p >= 1 && p <= total));
  return [...set].sort((a, b) => a - b);
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const STATUS_COLORS = {
  'New': '#3b82f6', 'Contacted': '#8b5cf6', 'Qualifying': '#f59e0b',
  'Qualified': '#22c55e', 'Proposal Sent': '#0ea5e9', 'Negotiating': '#e87860',
  'Converted': '#16a34a', 'Unqualified': '#94a3b8', 'Lost': '#ef4444',
};

const scoreColor = (v) => {
  const n = Number(v);
  if (n >= 80) return '#16a34a';
  if (n >= 60) return '#3b82f6';
  if (n >= 35) return '#f59e0b';
  return '#ef4444';
};


const Badge = ({ value, color }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '2px 10px', borderRadius: 99, whiteSpace: 'nowrap',
    background: (color || '#94a3b8') + '18',
    border: `1px solid ${(color || '#94a3b8')}40`,
    fontSize: 11, fontWeight: 600, color: color || '#94a3b8',
  }}>
    <span style={{ width: 6, height: 6, borderRadius: '50%', background: color || '#94a3b8', flexShrink: 0 }} />
    {value || '—'}
  </span>
);

const ScorePill = ({ score }) => {
  const n = Number(score);
  const color = scoreColor(n);
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: 99, background: color + '18', color,
      fontWeight: 700, fontSize: 12,
    }}>
      {isNaN(n) || score === '' || score === null ? '—' : n}
    </span>
  );
};

const SKEL = Array.from({ length: 6 });

// ── Component ─────────────────────────────────────────────────────────────────
const LeadListDetails = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [fetchVersion, setFetchVersion] = useState(0);
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  const refetch = useCallback(() => setFetchVersion(v => v + 1), []);

  useEffect(() => {
    let cancelled = false;

    const url = `${API_DOMAIN}${API_ROUTES.lead}?page=${page}&page_size=${pageSize}`;
    const applyData = (data) => {
      const items = Array.isArray(data) ? data : (data.results || []);
      setLeads(items);
      setTotalItems(Array.isArray(data) ? items.length : (data.count ?? items.length));
    };

    // Stale-while-revalidate: instant render from cache, background refresh only if stale.
    const cached = listCache.get(url);
    if (cached) {
      applyData(cached.data);
      setLoading(false);
    } else {
      setLoading(true);
    }

    if (!cached || cached.isStale) {
      listCache
        .fetchDeduped(url, buildHeaders())
        .then(data => {
          if (cancelled) return;
          listCache.set(url, data);
          applyData(data);
        })
        .catch(() => {
          if (cancelled) return;
          if (!cached) toast?.addToast({ type: 'error', message: 'Failed to load leads.' });
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }

    return () => { cancelled = true; };
  }, [page, pageSize, fetchVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    try {
      const r = await fetch(`${API_DOMAIN}${API_ROUTES.lead}${id}/`, {
        method: 'DELETE',
        headers: buildHeaders(),
      });
      if (!r.ok) throw new Error();
      toast?.addToast({ type: 'success', message: 'Lead deleted.' });
      listCache.invalidate((u) => u.startsWith(`${API_DOMAIN}${API_ROUTES.lead}`));
      refetch();
    } catch {
      toast?.addToast({ type: 'error', message: 'Failed to delete lead.' });
    }
  };

  // Client-side search filters within the current page
  const q = search.toLowerCase();
  const filtered = q
    ? leads.filter(l =>
        (l.title || '').toLowerCase().includes(q) ||
        (l.status || '').toLowerCase().includes(q) ||
        (l.lead_source || '').toLowerCase().includes(q) ||
        String(l.contact_data?.name || '').toLowerCase().includes(q)
      )
    : leads;

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const pages = pageRange(page, totalPages);
  const showFrom = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const showTo = Math.min(page * pageSize, totalItems);
  const goTo = (p) => setPage(Math.max(1, Math.min(p, totalPages)));
  const changePageSize = (n) => { setPageSize(n); setPage(1); };

  return (
    <div>
      <div className="card">
        {/* ── Search bar ── */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 340 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}>🔍</span>
            <input
              className="form-input"
              style={{ paddingLeft: 32 }}
              placeholder="Search by title, status, source…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            {loading
              ? 'Loading…'
              : q
                ? `${filtered.length.toLocaleString()} match${filtered.length !== 1 ? 'es' : ''} on this page`
                : `${totalItems.toLocaleString()} lead${totalItems !== 1 ? 's' : ''}`}
          </span>
        </div>

        {/* ── Table ── */}
        <div className="table-scroll">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Status</th>
                <th>Score</th>
                <th>Source</th>
                <th>Priority</th>
                <th>Created</th>
                <th style={{ textAlign: 'right', width: 1 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? SKEL.map((_, i) => (
                    <tr key={i}>
                      {[55, 60, 30, 50, 40, 55, 80].map((w, j) => (
                        <td key={j}><div className="skeleton" style={{ height: 13, width: `${w}%`, borderRadius: 4 }} /></td>
                      ))}
                    </tr>
                  ))
                : filtered.length === 0
                  ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="empty-state">
                          <div className="empty-state-icon-wrap">
                            {search ? (
                              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" />
                              </svg>
                            ) : (
                              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 8v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8" />
                                <path d="M3 8l2.5-4.5A1 1 0 0 1 6.37 3h11.26a1 1 0 0 1 .87.5L21 8" />
                                <path d="M3 8h18" /><path d="M9 12h6" />
                              </svg>
                            )}
                          </div>
                          <p className="empty-state-title">{search ? 'No matching records' : 'No leads yet'}</p>
                          <p className="empty-state-text">
                            {search ? `We couldn't find anything for "${search}" on this page.` : 'Click "New Lead" to create your first one.'}
                          </p>
                          {search && (
                            <button className="btn btn-secondary btn-sm" style={{ marginTop: 16 }} onClick={() => setSearch('')}>
                              Clear search
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                  : filtered.map(lead => (
                    <tr key={lead.id}>
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: 13, maxWidth: 220 }}>
                          {lead.title || lead.contact_data?.name || `Lead #${lead.id}`}
                        </div>
                        {lead.contact_data?.name && lead.title && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                            {lead.contact_data.name}
                          </div>
                        )}
                      </td>
                      <td><Badge value={lead.status} color={STATUS_COLORS[lead.status]} /></td>
                      <td><ScorePill score={lead.lead_score} /></td>
                      <td style={{ fontSize: 12, color: 'var(--text-sub)' }}>{lead.lead_source || '—'}</td>
                      <td>
                        {lead.priority
                          ? <span style={{ fontSize: 11, fontWeight: 700, color: lead.priority === 'High' ? '#b91c1c' : lead.priority === 'Medium' ? '#a16207' : '#15803d' }}>
                              {lead.priority}
                            </span>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>
                        }
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(lead.created_at)}</td>
                      <td>
                        <div className="table-actions">
                          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/lead/details/${btoa(lead.id)}`)}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/></svg>
                            View
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/lead/input/${btoa(lead.id)}`)}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z"/></svg>
                            Edit
                          </button>
                          {isSuperAdmin() && (
                            <button className="btn btn-danger btn-sm" onClick={() => setPendingDelete(lead.id)}>
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M2 4h12M6 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1M5 4v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V4"/></svg>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && totalItems > 0 && (
          <div className="pagination-wrap">
            <span className="pagination-info">
              {showFrom.toLocaleString()}–{showTo.toLocaleString()} of {totalItems.toLocaleString()}
            </span>

            <div className="pagination-buttons">
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(1)} disabled={page === 1} title="First">«</button>
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(page - 1)} disabled={page === 1} title="Previous">‹</button>
              {pages.map((p, idx) => {
                const prev = pages[idx - 1];
                return (
                  <React.Fragment key={p}>
                    {prev && p - prev > 1 && <span className="pagination-ellipsis">…</span>}
                    <button
                      className={`btn btn-sm pagination-page-btn${p === page ? ' active' : ''}`}
                      onClick={() => goTo(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(page + 1)} disabled={page === totalPages} title="Next">›</button>
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(totalPages)} disabled={page === totalPages} title="Last">»</button>
            </div>

            <div className="pagination-size-wrap">
              <label className="pagination-size-label">Rows:</label>
              <select
                className="pagination-size-select"
                value={pageSize}
                onChange={e => changePageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {isSuperAdmin() && (
        <ConfirmModal
          open={pendingDelete !== null}
          title="Delete Lead"
          message="Are you sure you want to delete this lead? This cannot be undone."
          confirmLabel="Delete"
          confirmDanger
          onConfirm={() => { handleDelete(pendingDelete); setPendingDelete(null); }}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
};

export default LeadListDetails;
